import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import { Button, ErrorModal, Loader, Modal } from '@/components/ui';
import { useWindowSize } from '@/hooks/index';
import { base64ToFile } from '@/utils/base64ToFile';
import { SuccessModal } from '@/components/ui/Modals/Success/SuccessModal';
import { actions as orderActions } from '@/domains/esim/order';
import { actions as numberSaleActions } from '@/domains/number-sale';
import { EModalType } from '@/enums/modal';

import styles from './RecordModal.module.scss';

const Sima = ({
  active,
  setActiveTab,
  handleCheck,
  isLoading,
  errorText,
  modalProps,
  closeModal,
  path,
}) => {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const [windowsX] = useWindowSize();
  const router = useRouter();
  const dispatch = useDispatch();

  const [imageSrc, setImageSrc] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(true);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setImageSrc(imageSrc);
      stopCamera();
    }
  }, [webcamRef]);

  const stopCamera = () => {
    if (webcamRef?.current?.video?.srcObject) {
      const stream = webcamRef.current.video.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      webcamRef.current.video.srcObject = null;
      setIsCameraActive(false);
    }
  };

  const retakePhoto = () => {
    setImageSrc(null);
    setIsCameraActive(true);
  };
  const closeSima = () => {
    dispatch(numberSaleActions.setActiveSima(false));
    dispatch(orderActions.setActiveSima(false));
  };
  const checkSima = async () => {
    const image = base64ToFile(imageSrc, 'image.jpg');
    handleCheck(image);
  };
  const handleSuccess = () => {
    closeModal();
    setActiveTab();
    closeSima();
  };

  const simaErrorHandle = () => {
    closeModal();
    closeSima();
    router.replace(path);
  };

  return (
    <>
      {active && !modalProps?.isOpen && (
        <Modal
          active={active}
          setActive={closeSima}
          className={styles.sima_wrap}
          itemScope
          itemType='https://schema.org/WebPage'
        >
          <Image
            src='/assets/esim/scan.svg'
            alt='check icon'
            width={51}
            height={51}
            layout='fixed'
          />
          <p className={styles.text}> {t('esim:common.cameraText')}</p>
          {isLoading ? (
            <Loader className={styles.sima_loader} />
          ) : (
            <div className={styles.camera_wrap}>
              {isCameraActive && (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  mirrored
                  screenshotFormat='image/jpeg'
                  style={{ width: 'auto', height: windowsX <= 480 ? 300 : 428 }}
                />
              )}
              {imageSrc && (
                <Image
                  src={imageSrc}
                  alt='user photo'
                  width={400}
                  height={434}
                  objectFit='cover'
                />
              )}
            </div>
          )}
          <div className={styles.sima_btn__wrapper}>
            {imageSrc ? (
              <>
                <Button
                  Component={'button'}
                  variant='outline'
                  onClick={retakePhoto}
                  disabled={isLoading}
                >
                  {t('esim:common.retakePhoto')}
                </Button>
                <Button
                  Component={'button'}
                  variant='normal'
                  onClick={checkSima}
                  disabled={isLoading}
                >
                  {t('common:Continue')}
                </Button>
              </>
            ) : (
              <div className={styles.bottom}>
                <Image
                  src='/assets/esim/sima.svg'
                  alt='check icon'
                  width={172}
                  height={80}
                  layout='fixed'
                />
                <Button
                  Component={'button'}
                  variant='normal'
                  onClick={capture}
                  disabled={!isCameraActive}
                >
                  {t('esim:common.takePhoto')}
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
      {modalProps?.type === EModalType.ERROR && modalProps?.isOpen && (
        <ErrorModal
          active={modalProps.isOpen}
          icon='/assets/error.png'
          setActive={simaErrorHandle}
        >
          {errorText ?? modalProps?.message}
        </ErrorModal>
      )}
      {modalProps?.type === EModalType.SUCCESS && modalProps?.isOpen && (
        <SuccessModal
          active={modalProps.isOpen}
          setSuccessModal={handleSuccess}
        >
          {t('cabinet:common.success')}
        </SuccessModal>
      )}
    </>
  );
};

export default Sima;
